import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  deleteSessionTemplate,
  getSessionTemplate,
  updateSessionTemplate,
} from '@/db/session-templates';
import { useFormErrorScroll } from '@/hooks/useFormErrorScroll';
import { toastError, toastPromise } from '@/lib/toast-helpers';
import {
  GameConfigSchema,
  parseSessionTemplateId,
  safeParseSessionTemplateId,
  type GameConfig,
} from '@/schemas';

const optionalPositiveNumber = z
  .union([z.number().int().positive(), z.nan()])
  .transform((val): number | undefined => {
    if (Number.isNaN(val)) {
      return undefined;
    }
    return val;
  })
  .optional();

const EditSessionTemplateSchema = z.object({
  name: z
    .string()
    .min(1, 'Template name is required')
    .max(200, 'Name must be 200 characters or less'),
  description: z.string().max(2000, 'Description must be 2000 characters or less').optional(),
  defaultSessionName: z
    .string()
    .max(200, 'Default session name must be 200 characters or less')
    .optional(),
  // Game config fields
  hasRounds: z.boolean(),
  winCondition: z.enum(['mostPoints', 'mostRounds']),
  roundTargetPoints: optionalPositiveNumber,
  gameTargetPoints: optionalPositiveNumber,
  targetRounds: optionalPositiveNumber,
  teamScoringType: z.enum(['combined', 'individual']).optional(),
  version: z.number().int().positive(),
});

type EditSessionTemplateForm = z.infer<typeof EditSessionTemplateSchema>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
export const Route = createFileRoute('/templates/$templateId/edit' as any)({
  loader: ({ params }) => {
    const parseResult = safeParseSessionTemplateId(String(params.templateId));
    if (!parseResult.success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- notFound() is TanStack Router's official API for 404 handling, not a standard Error
      throw notFound();
    }
  },
  component: EditSessionTemplatePage,
});

function EditSessionTemplatePage() {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Route tree will be regenerated after build
  const { templateId: templateIdParam } = Route.useParams();
  const templateId = String(templateIdParam);
  const navigate = useNavigate();
  const { t } = useTranslation('sessions');
  const { t: tCommon } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditSessionTemplateForm>({
    resolver: zodResolver(EditSessionTemplateSchema),
  });

  useFormErrorScroll(errors);

  const watchedHasRounds = watch('hasRounds');
  const watchedWinCondition = watch('winCondition');
  const watchedName = watch('name');
  const watchedDescription = watch('description');

  // Auto-set winCondition when hasRounds changes
  useEffect(() => {
    if (!watchedHasRounds) {
      setValue('winCondition', 'mostPoints');
    }
  }, [watchedHasRounds, setValue]);

  const loadTemplate = useCallback(async () => {
    try {
      const brandedTemplateId = parseSessionTemplateId(String(templateId));
      const template = await getSessionTemplate(brandedTemplateId);
      if (!template) {
        setError('Session template not found');
        setIsLoading(false);
        return;
      }

      const gameConfig = template.gameConfig;

      reset({
        name: template.name,
        description: template.description || '',
        defaultSessionName: template.defaultSessionName || '',
        hasRounds: gameConfig.hasRounds,
        winCondition: gameConfig.winCondition,
        roundTargetPoints: gameConfig.roundTargetPoints,
        gameTargetPoints: gameConfig.gameTargetPoints,
        targetRounds: gameConfig.targetRounds,
        teamScoringType: gameConfig.teamScoringType,
        version: gameConfig.version,
      });

      document.title = `${t('templates.edit.title', 'Edit Template')} ${template.name} - ${tCommon('app.name', 'One Score')}`;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load session template';
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [templateId, reset, t, tCommon]);

  useEffect(() => {
    void loadTemplate();
  }, [loadTemplate]);

  const onSubmit = async (data: EditSessionTemplateForm) => {
    try {
      setIsSaving(true);
      setError(undefined);

      const brandedTemplateId = parseSessionTemplateId(String(templateId));

      const gameConfig: GameConfig = GameConfigSchema.parse({
        hasRounds: data.hasRounds,
        winCondition: data.winCondition,
        roundTargetPoints: data.roundTargetPoints,
        gameTargetPoints: data.gameTargetPoints,
        targetRounds: data.targetRounds,
        teamScoringType: data.teamScoringType,
        version: data.version,
      });

      const updatePromise = updateSessionTemplate(brandedTemplateId, {
        name: data.name.trim(),
        description: data.description?.trim() || undefined,
        defaultSessionName: data.defaultSessionName?.trim() || undefined,
        gameConfig,
      });

      await toastPromise(updatePromise, {
        loading: t('templates.edit.saving', 'Saving...'),
        success: t('templates.edit.success', 'Template updated successfully'),
        error: (error: unknown) =>
          error instanceof Error
            ? error.message
            : t('templates.edit.error', 'Failed to update template'),
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
      void navigate({ to: '/templates' as any });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('templates.edit.error');
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError(undefined);

      const brandedTemplateId = parseSessionTemplateId(String(templateId));

      const deletePromise = deleteSessionTemplate(brandedTemplateId);

      await toastPromise(deletePromise, {
        loading: t('templates.delete.deleting', 'Deleting...'),
        success: t('templates.delete.success', 'Template deleted successfully'),
        error: (error: unknown) =>
          error instanceof Error
            ? error.message
            : t('templates.delete.error', 'Failed to delete template'),
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
      void navigate({ to: '/templates' as any });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('templates.delete.error');
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl space-y-6 px-4 mx-auto py-8">
        <div className="space-y-4">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error && !watch('name')) {
    return (
      <div className="w-full max-w-2xl space-y-6 px-4 mx-auto py-8">
        <div className="space-y-4">
          <p className="text-destructive">{error}</p>
          <Button
            variant="outline"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
            onClick={() => void navigate({ to: '/templates' as any })}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('templates.edit.back', 'Back to Templates')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl space-y-6 px-4 mx-auto py-8">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
          onClick={() => void navigate({ to: '/templates' as any })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('templates.edit.title', 'Edit Template')}
          </h1>
          <p className="text-muted-foreground">
            {t('templates.edit.description', 'Update template details')}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="space-y-6">
        <Card className="p-6">
          <div className="space-y-6">
            {/* Template Info Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">{t('templates.edit.name', 'Template Name')} *</Label>
                <Input
                  id="template-name"
                  placeholder={t('templates.edit.namePlaceholder', 'Enter template name')}
                  {...register('name')}
                  disabled={isSaving}
                  maxLength={200}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                {200 - (watchedName?.length ?? 0) < 20 && (
                  <p className="text-xs text-muted-foreground">
                    {tCommon('form.charactersRemaining', {
                      count: 200 - (watchedName?.length ?? 0),
                    })}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-description">
                  {t('templates.edit.descriptionLabel', 'Description')}
                </Label>
                <Textarea
                  id="template-description"
                  placeholder={t(
                    'templates.edit.descriptionPlaceholder',
                    'Optional description...',
                  )}
                  rows={3}
                  {...register('description')}
                  disabled={isSaving}
                  maxLength={2000}
                  className={errors.description ? 'border-destructive' : ''}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
                {2000 - (watchedDescription?.length ?? 0) < 20 && (
                  <p className="text-xs text-muted-foreground">
                    {tCommon('form.charactersRemaining', {
                      count: 2000 - (watchedDescription?.length ?? 0),
                    })}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="default-session-name">
                  {t('templates.edit.defaultSessionName', 'Default Session Name')}
                </Label>
                <Input
                  id="default-session-name"
                  placeholder={t(
                    'templates.edit.defaultSessionNamePlaceholder',
                    'Optional default name for new sessions...',
                  )}
                  {...register('defaultSessionName')}
                  disabled={isSaving}
                  maxLength={200}
                />
                {errors.defaultSessionName && (
                  <p className="text-sm text-destructive">{errors.defaultSessionName.message}</p>
                )}
              </div>
            </div>

            {/* Game Configuration Section */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="text-lg font-semibold">
                {tCommon('games.form.configuration', 'Game Configuration')}
              </h3>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasRounds"
                  checked={watchedHasRounds}
                  onCheckedChange={(checked) => {
                    setValue('hasRounds', checked === true);
                  }}
                  disabled={isSaving}
                />
                <Label htmlFor="hasRounds" className="cursor-pointer font-normal">
                  {tCommon('games.form.hasRounds', 'Has Rounds')}
                </Label>
              </div>
              <p className="text-sm text-muted-foreground ml-6">
                {tCommon('games.form.hasRounds.desc', 'Track scores per round')}
              </p>

              {watchedHasRounds && (
                <div className="space-y-4 ml-6">
                  <Label>{tCommon('winCondition', 'Win Condition')} *</Label>
                  <RadioGroup
                    value={watchedWinCondition}
                    onValueChange={(value) => {
                      setValue('winCondition', value as 'mostPoints' | 'mostRounds', {
                        shouldValidate: true,
                      });
                    }}
                    disabled={isSaving}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="mostPoints" id="win-most-points" />
                      <Label htmlFor="win-most-points" className="cursor-pointer font-normal">
                        {tCommon('mostPoints', 'Most Points')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="mostRounds" id="win-most-rounds" />
                      <Label htmlFor="win-most-rounds" className="cursor-pointer font-normal">
                        {tCommon('mostRounds', 'Most Rounds')}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}
            </div>

            {/* Optional Targets Section */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="text-lg font-semibold">
                {tCommon('games.form.targets', 'Optional Targets')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {tCommon(
                  'games.form.targets.desc',
                  'Set target goals to show visual indicators in sessions',
                )}
              </p>

              <div className="grid gap-6 md:grid-cols-2 space-y-0">
                <div className="space-y-2">
                  <Label htmlFor="roundTargetPoints">
                    {tCommon('games.form.roundTargetPoints', 'Target Points Per Round (Optional)')}
                  </Label>
                  <Input
                    id="roundTargetPoints"
                    type="number"
                    {...register('roundTargetPoints', {
                      valueAsNumber: true,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.valueAsNumber;
                        setValue('roundTargetPoints', Number.isNaN(val) ? undefined : val);
                      },
                    })}
                    disabled={isSaving || !watchedHasRounds}
                    placeholder={watchedHasRounds ? undefined : 'Enable rounds'}
                  />
                  {watchedHasRounds && (
                    <p className="text-xs text-muted-foreground">
                      {tCommon(
                        'games.form.roundTargetPoints.desc',
                        'Show indicator when reached in a round',
                      )}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gameTargetPoints">
                    {tCommon('games.form.gameTargetPoints', 'Target Points (Game, Optional)')}
                  </Label>
                  <Input
                    id="gameTargetPoints"
                    type="number"
                    {...register('gameTargetPoints', {
                      valueAsNumber: true,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.valueAsNumber;
                        setValue('gameTargetPoints', Number.isNaN(val) ? undefined : val);
                      },
                    })}
                    disabled={isSaving}
                  />
                  <p className="text-xs text-muted-foreground">
                    {tCommon(
                      'games.form.gameTargetPoints.desc',
                      'Show indicator when total score reached',
                    )}
                  </p>
                </div>

                {watchedHasRounds && (
                  <div className="space-y-2">
                    <Label htmlFor="targetRounds">
                      {tCommon('games.form.targetRounds', 'Target Rounds (Optional)')}
                    </Label>
                    <Input
                      id="targetRounds"
                      type="number"
                      {...register('targetRounds', {
                        valueAsNumber: true,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.valueAsNumber;
                          setValue('targetRounds', Number.isNaN(val) ? undefined : val);
                        },
                      })}
                      disabled={isSaving}
                    />
                    <p className="text-xs text-muted-foreground">
                      {tCommon(
                        'games.form.targetRounds.desc',
                        'Show indicator when this many rounds won',
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Team Configuration Section */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="text-lg font-semibold">
                {tCommon('games.form.enableTeams', 'Enable Teams')}
              </h3>
              <div className="space-y-4">
                <Label>{tCommon('games.form.teamScoringType', 'Team Scoring')}</Label>
                <RadioGroup
                  value={watch('teamScoringType') || 'none'}
                  onValueChange={(value) => {
                    setValue(
                      'teamScoringType',
                      value === 'none' ? undefined : (value as 'combined' | 'individual'),
                      {
                        shouldValidate: true,
                      },
                    );
                  }}
                  disabled={isSaving}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="none" id="team-none" />
                    <Label htmlFor="team-none" className="cursor-pointer font-normal">
                      {tCommon('games.form.teamScoringType.none', 'No Teams')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="combined" id="team-combined" />
                    <Label htmlFor="team-combined" className="cursor-pointer font-normal">
                      {tCommon('games.form.teamScoringType.combined', 'Combined')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="individual" id="team-individual" />
                    <Label htmlFor="team-individual" className="cursor-pointer font-normal">
                      {tCommon('games.form.teamScoringType.individual', 'Individual')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isSaving || isDeleting}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('templates.edit.delete', 'Delete Template')}
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
              onClick={() => void navigate({ to: '/templates' as any })}
              disabled={isSaving || isDeleting}
            >
              {t('create.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isSaving || isDeleting}>
              {isSaving
                ? t('templates.edit.saving', 'Saving...')
                : t('templates.edit.save', 'Save Changes')}
            </Button>
          </div>
        </div>
      </form>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('templates.delete.title', 'Delete Template?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'templates.delete.description',
                'This action cannot be undone. The template will be permanently deleted.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('create.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting
                ? t('templates.delete.deleting', 'Deleting...')
                : t('templates.delete.confirm', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
